import { LkReactMenuId, Permission, PermissionAction } from 'generated/openApi';
import { CardId, CardPermissionRequirement, LkDashboardCardId } from '../types/DashboardCardConfig';

// Role-level gating: card ID → required permissions
export const CARD_PERMISSION_MAP: Record<number, CardPermissionRequirement[]> = {
    [LkDashboardCardId.MatchChart]: [{ permission: Permission.MatchReport, action: PermissionAction.View }],
    [LkDashboardCardId.MatchTable]: [{ permission: Permission.MatchReport, action: PermissionAction.View }],
    [LkDashboardCardId.TotalStudents]: [{ permission: Permission.StudentEnrollment, action: PermissionAction.View }],
    [LkDashboardCardId.SchoolEnrollment]: [{ permission: Permission.StudentEnrollment, action: PermissionAction.View }],
    [LkDashboardCardId.EndReasonTypeTable]: [{ permission: Permission.StudentEnrollment, action: PermissionAction.View }],
    [LkDashboardCardId.EndReasonTypeChart]: [{ permission: Permission.StudentEnrollment, action: PermissionAction.View }],
    [LkDashboardCardId.EligibilityChart]: [{ permission: Permission.Student, action: PermissionAction.View }],
    [LkDashboardCardId.EligibilityTable]: [{ permission: Permission.Student, action: PermissionAction.View }],
    [LkDashboardCardId.IEPChart]: [{ permission: Permission.StudentEnrollment, action: PermissionAction.View }],
    [LkDashboardCardId.IEPTable]: [{ permission: Permission.StudentEnrollment, action: PermissionAction.View }],
    [LkDashboardCardId.LEPChart]: [{ permission: Permission.StudentEnrollment, action: PermissionAction.View }],
    [LkDashboardCardId.LEPTable]: [{ permission: Permission.StudentEnrollment, action: PermissionAction.View }],
    [LkDashboardCardId.EnrollmentByAdvisorChart]: [{ permission: Permission.StudentEnrollment, action: PermissionAction.View }],
    [LkDashboardCardId.EnrollmentByAdvisorTable]: [{ permission: Permission.StudentEnrollment, action: PermissionAction.View }],
};

// Tenant-level gating: card ID → required LkReactMenuId
// Cards omitted from this map are visible to all tenants
export const CARD_TENANT_MAP: Partial<Record<number, number>> = {
    [LkDashboardCardId.ServiceChart]: LkReactMenuId.Students_Services,
    [LkDashboardCardId.ServiceTable]: LkReactMenuId.Students_Services,
    [LkDashboardCardId.MatchChart]: LkReactMenuId.Match_Reports,
    [LkDashboardCardId.MatchTable]: LkReactMenuId.Match_Reports,
    [LkDashboardCardId.TotalStudents]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.SchoolEnrollment]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.FafsaChart]: LkReactMenuId.Students_Fafsa,
    [LkDashboardCardId.FafsaTable]: LkReactMenuId.Students_Fafsa,
    [LkDashboardCardId.EndReasonTypeTable]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.EndReasonTypeChart]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.ParticipantsChart]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.ParticipantsTable]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.GenderChart]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.GenderTable]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.EligibilityChart]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.EligibilityTable]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.IEPChart]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.IEPTable]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.LEPChart]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.LEPTable]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.GradeLevelTable]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.GradeLevelChart]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.StudentServiceChart]: LkReactMenuId.Students_Services,
    [LkDashboardCardId.StudentServiceTable]: LkReactMenuId.Students_Services,
    [LkDashboardCardId.GPAChart]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.GPATable]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.EnrollmentByAdvisorChart]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.EnrollmentByAdvisorTable]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.GPASubmissionChart]: LkReactMenuId.Students_Students,
    [LkDashboardCardId.GPASubmissionTable]: LkReactMenuId.Students_Students,
};

export interface CardPairVariant {
    cardId: CardId;
    chipLabel: string;
}

export interface CardPairGroup {
    groupName: string;
    description: string;
    icon: string;
    variants: CardPairVariant[];
}

export const CARD_PAIR_GROUPS: CardPairGroup[] = [
    {
        groupName: 'GPA', description: 'GPA submission rates by school, grade, and term', icon: 'ShowChart',
        variants: [
            { cardId: LkDashboardCardId.GPASubmissionChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.GPASubmissionTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'Enrollment by Advisor', description: 'Student enrollment grouped by advisor', icon: 'HowToReg',
        variants: [
            { cardId: LkDashboardCardId.EnrollmentByAdvisorChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.EnrollmentByAdvisorTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'GPA Distribution', description: 'Student GPA distribution by school year', icon: 'ShowChart',
        variants: [
            { cardId: LkDashboardCardId.GPAChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.GPATable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'Annual Services', description: 'Participation rates and average service hours', icon: 'VolunteerActivism',
        variants: [
            { cardId: LkDashboardCardId.StudentServiceChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.StudentServiceTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'Grade Level', description: 'Grade-level distribution by school year', icon: 'School',
        variants: [
            { cardId: LkDashboardCardId.GradeLevelChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.GradeLevelTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'Monthly Services',
        description: 'Service distribution overview',
        icon: 'SettingsSuggest',
        variants: [
            { cardId: LkDashboardCardId.ServiceChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.ServiceTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'Match',
        description: 'Match activity overview',
        icon: 'Handshake',
        variants: [
            { cardId: LkDashboardCardId.MatchChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.MatchTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'Enrollment',
        description: 'Student enrollment overview',
        icon: 'HowToReg',
        variants: [
            { cardId: LkDashboardCardId.SchoolEnrollment, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.TotalStudents, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'FAFSA',
        description: 'FAFSA submission data',
        icon: 'RequestQuote',
        variants: [
            { cardId: LkDashboardCardId.FafsaChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.FafsaTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'End Reason Type',
        description: 'Student end reason breakdown',
        icon: 'ExitToApp',
        variants: [
            { cardId: LkDashboardCardId.EndReasonTypeChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.EndReasonTypeTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'Participants',
        description: 'Participant status overview',
        icon: 'PeopleAlt',
        variants: [
            { cardId: LkDashboardCardId.ParticipantsChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.ParticipantsTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'Gender',
        description: 'Gender distribution overview',
        icon: 'Wc',
        variants: [
            { cardId: LkDashboardCardId.GenderChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.GenderTable, chipLabel: 'Table' },
        ],
    },
        {
        groupName: 'Race',
        description: 'Race distribution overview',
        icon: 'Wc',
        variants: [
            { cardId: LkDashboardCardId.RaceChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.RaceTable, chipLabel: 'Table' },
        ],
    },
        {
        groupName: 'Ethnicity',
        description: 'Ethnicity distribution overview',
        icon: 'Wc',
        variants: [
            { cardId: LkDashboardCardId.EthnicityChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.EthnicityTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'Eligibility Criteria',
        description: 'Student eligibility overview',
        icon: 'Checklist',
        variants: [
            { cardId: LkDashboardCardId.EligibilityChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.EligibilityTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'IEP',
        description: 'IEP student enrollment overview',
        icon: 'Checklist',
        variants: [
            { cardId: LkDashboardCardId.IEPChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.IEPTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'LEP',
        description: 'LEP student enrollment overview',
        icon: 'School',
        variants: [
            { cardId: LkDashboardCardId.LEPChart, chipLabel: 'Chart' },
            { cardId: LkDashboardCardId.LEPTable, chipLabel: 'Table' },
        ],
    },
    {
        groupName: 'Notifications',
        description: 'Recent notifications and updates',
        icon: 'NotificationsActive',
        variants: [
            { cardId: LkDashboardCardId.Notifications, chipLabel: 'Enabled' },
        ],
    },
];
